import { Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import type { DropSlotProps } from './DropSlot';
import { useDragDropAnswerContext } from './DragDropAnswerContext';
import { OptionChip } from './OptionChip';
import { useChipClone } from '@/hooks/questions/dnd/useChipClone';

export function MultipleDropSlot({
  id,
  inline = false,
  ariaLabel,
  className,
}: DropSlotProps) {
  const { tokensOf, disabled, onContainerClick, selectedId, flow } =
    useDragDropAnswerContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'questionView.dnd',
  });
  const tokens = tokensOf(id);
  const isEmpty = tokens.length === 0;
  const marginByFlow = flow === 'vertical' ? 'mb-2' : 'mr-2';
  const renderClone = useChipClone();

  return (
    <Droppable
      droppableId={id}
      direction={flow}
      isDropDisabled={disabled}
      renderClone={renderClone}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          data-testid={`slot-${id}`}
          aria-label={ariaLabel ?? t('slotLabel')}
          aria-roledescription={t('dropZoneRoleDescription')}
          onClick={event => {
            if (event.defaultPrevented) return;
            onContainerClick(id);
          }}
          className={twMerge(
            'relative flex min-h-12 min-w-24 items-center rounded-lg p-1',
            inline ? 'inline-flex align-middle' : 'flex',
            flow === 'vertical' && 'flex-col',
            tokens.length > 0 ? 'min-w-auto' : '',
            isEmpty && 'border border-dashed border-secondary',
            selectedId && 'cursor-pointer',
            snapshot.isDraggingOver && 'border border-secondary bg-secondary/5',
            className,
          )}
        >
          {tokens.map((token, index) => (
            <OptionChip
              key={token.id}
              token={token}
              index={index}
              className={marginByFlow}
            />
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}
